import React from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { useLanguage } from '../../../../contexts/LanguageContext';
import useOrderRequestsFilter from './OrderRequestsFilter';

const columnsConfig = [
    { field: 'dmsOrderNumber', minWidth: '140px', type: 'text' },
    { field: 'dealerName', minWidth: '220px', type: 'text' },
    { field: 'brand', minWidth: '130px', type: 'text' },
    { field: 'model', minWidth: '130px', type: 'text' },
    { field: 'matnr', minWidth: '170px', type: 'text' },
    { field: 'maktx', minWidth: '180px', type: 'text' },
    { field: 'quantity', minWidth: '110px', type: 'text' },
    { field: 'deliveryDate', minWidth: '160px', type: 'date' },
    { field: 'paymentDate', minWidth: '160px', type: 'date' },
    { field: 'statusText', headerKey: 'status', minWidth: '140px', type: 'text' },
    { field: 'createdDate', minWidth: '160px', type: 'date' },
    { field: 'createdName', minWidth: '210px', type: 'text' },
    { field: 'modifiedDate', minWidth: '160px', type: 'date', allRequestsOnly: true },
    { field: 'modifiedName', minWidth: '210px', type: 'text', allRequestsOnly: true }
];

const OrderRequestsTable = ({ data = [], loading = false, activeTabKey, onFilteredCountChange, onView }) => {
    const { th, getCurrentLocale } = useLanguage();
    const hasActiveFilterRef = React.useRef(false);
    const [activeFilters, setActiveFilters] = React.useState({});
    const { filters, onFilter, getColumnFilterProps } = useOrderRequestsFilter({
        activeFilters,
        onFiltersChange: setActiveFilters
    });

    const toDate = (value) => {
        if (!value) return null;
        const date = value instanceof Date ? value : new Date(value);
        return Number.isNaN(date.getTime()) ? null : date;
    };

    const tableData = data.map((item) => ({
        ...item,
        deliveryDate: toDate(item.deliveryDate),
        paymentDate: toDate(item.paymentDate),
        createdDate: toDate(item.createdDate),
        modifiedDate: toDate(item.modifiedDate)
    }));

    const formatDate = (value) => {
        if (!value) return '-';
        try {
            return new Date(value).toLocaleDateString(getCurrentLocale(), { day: '2-digit', month: '2-digit', year: 'numeric' });
        } catch {
            return value;
        }
    };

    const actionBodyTemplate = (rowData) => (
        <Button
            icon="pi pi-eye"
            className="p-button-text p-button-rounded p-button-info"
            onClick={() => onView?.(rowData)}
            tooltip={th('vehicleSalesOrderRequests.detail.title')}
            tooltipOptions={{ position: 'top' }}
        />
    );

    const handleFilter = (event) => {
        onFilter(event);
        const hasActiveFilter = Object.values(event.filters ?? {}).some((filter) => {
            const values = filter?.constraints?.map((constraint) => constraint?.value) ?? [filter?.value];
            return values.some((value) => value !== null && value !== undefined && value !== '');
        });

        hasActiveFilterRef.current = hasActiveFilter;
        if (!hasActiveFilter) onFilteredCountChange?.(null);
    };

    return (
        <DataTable
            value={tableData}
            paginator
            rows={10}
            rowsPerPageOptions={[5, 10, 25, 50]}
            loading={loading}
            showGridlines
            stripedRows
            responsiveLayout="scroll"
            filterDisplay="row"
            filters={filters}
            onFilter={handleFilter}
            onValueChange={(value) => {
                if (hasActiveFilterRef.current) {
                    onFilteredCountChange?.(value.length);
                }
            }}
            emptyMessage={th('common.noRecords')}
            dataKey="id"
            className="p-datatable-gridlines"
            tableStyle={{ minWidth: activeTabKey === 'all' ? '154rem' : '126rem' }}
        >
            <Column
                header={th('vehicleSalesOrderRequests.columns.actions')}
                body={actionBodyTemplate}
                style={{ width: '80px', textAlign: 'center' }}
                frozen
                alignFrozen="left"
            />
            {columnsConfig
                .filter(({ allRequestsOnly }) => !allRequestsOnly || activeTabKey === 'all')
                .map(({ field, headerKey = field, minWidth, type }) => (
                    <Column
                        key={field}
                        field={field}
                        header={th(`vehicleSalesOrderRequests.columns.${headerKey}`)}
                        body={type === 'date' ? (row) => formatDate(row[field]) : undefined}
                        sortable
                        style={{ minWidth }}
                        dataType={type === 'date' ? 'date' : undefined}
                        {...getColumnFilterProps(type)}
                    />
                ))}
        </DataTable>
    );
};

export default OrderRequestsTable;
